import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useLang } from "../../i18n/LanguageProvider";

const AVATARS = [
  "https://images.unsplash.com/photo-1492462543947-040389c4a66c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1548213238-0da7521bd6e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1527980965255-d3b416303d12?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
];

export const Testimonials = () => {
  const { t } = useLang();

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#F5F1E8] text-[#0B1416] py-28 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-end justify-between gap-6 flex-wrap"
        >
          <div>
            <div className="overline text-[#0D7A7D]">— {t.testimonials.eyebrow}</div>
            <h2
              data-testid="testimonials-title"
              className="font-heading-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-6 max-w-3xl"
            >
              {t.testimonials.title}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} fill="#1BB6B9" stroke="#1BB6B9" />
            ))}
            <span className="ml-3 overline">4.9 / 5</span>
          </div>
        </motion.div>
      </div>

      {/* Editorial marquee — massive italic quotes */}
      <div className="relative">
        <Marquee gradient={false} speed={22} pauseOnHover>
          {t.testimonials.list.map((item, i) => (
            <div
              key={i}
              data-testid={`testimonial-${i}`}
              className="flex items-center gap-8 md:gap-12 pl-12 md:pl-24 pr-4"
            >
              <div className="w-16 h-16 md:w-24 md:h-24 shrink-0 clip-frame">
                <img
                  src={AVATARS[i % AVATARS.length]}
                  alt={item.n}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="max-w-[70vw]">
                <div
                  className="font-italic leading-[1.05]"
                  style={{ fontSize: "clamp(36px, 5.5vw, 88px)" }}
                >
                  “{item.q}”
                </div>
                <div className="flex items-center gap-3 mt-4 text-[11px] uppercase tracking-[0.28em]">
                  <span className="text-[#0D7A7D]">— {item.n}</span>
                  <span className="opacity-40">|</span>
                  <span className="opacity-60">{item.c}</span>
                </div>
              </div>
              <span className="text-[#1BB6B9] mx-8" style={{ fontSize: 8 }}>
                ●
              </span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* Google reviews CTA */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 mt-20">
        <motion.a
          href="https://www.google.com/search?q=Travel+To+Time+Matar%C3%B3"
          target="_blank"
          rel="noreferrer"
          data-testid="google-reviews-link"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.28em] border-b border-[#0B1416] pb-2 hover:text-[#0D7A7D] hover:border-[#0D7A7D] transition-colors"
        >
          {t.footer.google_cta}
          <span className="opacity-60">→</span>
        </motion.a>
      </div>
    </section>
  );
};
